import { useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, ProgressBar, Row, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlask, faInfoCircle, faRotate, faStop } from '@fortawesome/free-solid-svg-icons';
import { confirmAlert } from 'react-confirm-alert';
import { ShowConfirm } from './Alerts.js';
import { LOAD_TEST_REPORT_ATTEMPTS } from '../hooks/useLoadTestFlow.js';
import { redactLoadTestDiagnostic } from '../utils/loadTestFeedback.js';

export const loadTestElapsedLabel = (startedAt, finishedAt = null) => {
    if (!startedAt) return '0s';
    const end = finishedAt ? new Date(finishedAt).getTime() : Date.now();
    const seconds = Math.max(0, Math.floor((end - new Date(startedAt).getTime()) / 1000));
    return seconds >= 60 ? `${Math.floor(seconds / 60)}min ${seconds % 60}s` : `${seconds}s`;
};

export const LoadTestBadge = ({ variant = 'primary', children }) => (
    <Badge bg={variant} className={['primary', 'light', 'warning', 'info'].includes(variant) ? 'text-black' : undefined}>{children}</Badge>
);

export const LoadTestNotice = ({ children }) => (
    <Alert variant="primary" className="p-2 mb-3 d-flex align-items-start">
        <FontAwesomeIcon icon={faInfoCircle} size="sm" className="me-2 mt-1 flex-shrink-0" />
        <div>
            <strong className="d-block mb-1">Os dados existentes serão preservados.</strong>
            <span>{children}</span>
        </div>
    </Alert>
);

export const LoadTestBotCount = ({ value, onChange, minimum = 1, maximum = 2000, children }) => (
    <Form.Group className="mb-3">
        <Form.Label>Número de bots simultâneos</Form.Label>
        <Form.Control type="number" min={minimum} max={maximum} step={1} value={value}
            onChange={(event) => onChange(event.target.value)}
            isInvalid={value !== '' && (!Number.isInteger(Number(value)) || Number(value) < minimum || Number(value) > maximum)} autoFocus />
        <Form.Control.Feedback type="invalid">Informe um número inteiro entre {minimum} e {maximum.toLocaleString('pt-BR')}.</Form.Control.Feedback>
        <Form.Text className="text-muted mt-2 d-block form-text">Limite: até {maximum.toLocaleString('pt-BR')} bots simultâneos.</Form.Text>
        {children}
    </Form.Group>
);

export const LoadTestSetupSummary = ({ items, covered, excluded }) => (
    <Card bg="light" border="0" className="mt-3 mb-3">
        <Card.Body className="py-2">
            <Row className="small g-2">
                {items.map(([label, value]) => <Col xs={6} key={label}><span className="text-muted d-block">{label}</span><strong>{value}</strong></Col>)}
            </Row>
            <hr className="my-2" />
            <div className="small"><strong>Coberto:</strong> {covered}</div>
            <div className="small text-muted"><strong>Excluído:</strong> {excluded}</div>
        </Card.Body>
    </Card>
);

export const LoadTestMetricCard = ({ title, value, subtitle, xs, sm = 6, md = 4, xl = md, horizontal = false }) => (
    <Col xs={xs} sm={sm} md={md} xl={xl} className="mb-2">
        <Card className={`h-100${horizontal ? ' streaming-load-test-metric-card-horizontal' : ''}`}>
            <Card.Body className={horizontal ? 'streaming-load-test-metric-card-body' : 'py-2'}>
                <small className="text-muted d-block">{title}</small>
                <strong className={horizontal ? 'streaming-load-test-metric-card-value' : 'd-block fs-4'}>{value}</strong>
                {subtitle && <span className="small text-muted">{subtitle}</span>}
            </Card.Body>
        </Card>
    </Col>
);

export const LoadTestProgress = ({ flow, phaseLabels, targetConnections, estimate, children }) => {
    const { job, running, stage } = flow;
    if (!job || stage === 'result') return null;
    const phase = String(job.phase || job.status || 'reserved');
    const progress = Math.max(0, Math.min(100, Number(job.progress || 0)));
    return <>
        <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
            <div><small className="text-muted">Fase atual</small><h5 className="mb-0">{stage === 'analysis' ? 'Analisando resultado' : phaseLabels[phase] || phase}</h5></div>
            <LoadTestBadge>{Number(targetConnections || 0).toLocaleString('pt-BR')} BOTS</LoadTestBadge>
        </div>
        <ProgressBar now={progress} label={`${Math.round(progress)}%`} animated={running} striped={running} className="mb-2 streaming-load-test-progress" />
        <div className="small text-muted mb-3">
            <div>{stage === 'analysis' ? 'Aguardando o relatório e a confirmação das validações.' : job.phase_message || 'Acompanhando os bots.'}</div>
            <div>{loadTestElapsedLabel(job.started_at || job.created_at, job.finished_at)}{estimate ? ` · estimativa de término: ${estimate}` : ''}</div>
        </div>
        {job.metadata && <Row className="g-2 mb-3 streaming-load-test-metric-row">
            <LoadTestMetricCard title="Conexões prontas" value={Number(job.metadata.ready || 0).toLocaleString('pt-BR')} subtitle={`${Number(job.metadata.active || 0).toLocaleString('pt-BR')} ativas agora`} xs={12} sm={4} md={4} xl={4} horizontal />
            <LoadTestMetricCard title="Participantes" value={Number(job.metadata.identity?.participant_authenticated || 0).toLocaleString('pt-BR')} subtitle={`${Number(job.metadata.identity?.participant_replaced || 0).toLocaleString('pt-BR')} substituídos`} xs={12} sm={4} md={4} xl={4} horizontal />
            <LoadTestMetricCard title="Visitantes" value={Number(job.metadata.identity?.visitor_authenticated || 0).toLocaleString('pt-BR')} subtitle="Completam a meta" xs={12} sm={4} md={4} xl={4} horizontal />
        </Row>}
        {stage === 'execution' && children}
    </>;
};

export const LoadTestResult = ({ outcome, partial, comparisonLabel, description, children }) => (
    <div className="streaming-load-test-results mt-2">
        <h5>Resultado {partial ? 'parcial' : 'final'}</h5>
        <Alert variant={outcome.variant} className="p-2 d-block" role="status">
            <div className="d-flex flex-wrap gap-2 align-items-center mb-1">
                <LoadTestBadge variant={outcome.variant}>Teste: {outcome.label}</LoadTestBadge>
                {comparisonLabel && <LoadTestBadge>Comparação: {comparisonLabel}</LoadTestBadge>}
            </div>
            <div>{outcome.message}</div>
        </Alert>
        {outcome.failed && outcome.failedChecks.length > 0 && <Alert variant="danger" className="p-2 d-block">
            <div><strong>Validações reprovadas:</strong> {outcome.failedChecks.join(', ')}.</div>
            {description && <div className="mt-1">{description}</div>}
        </Alert>}
        {outcome.incomplete && <Alert variant="primary" className="p-2 d-block">
            <div><strong>Evidências pendentes:</strong> {outcome.incompleteReasons.join(', ') || 'veredito final das validações'}.</div>
            {description && <div className="mt-1">{description}</div>}
        </Alert>}
        {(!outcome.failed || !outcome.failedChecks.length) && !outcome.incomplete && description && <div className="small text-muted mb-3">{description}</div>}
        {children}
        {outcome.warnings.length > 0 && <Alert variant="primary" className="p-2 mt-3 d-block">
            <strong>Avisos:</strong>{outcome.warnings.map((warning) => <div key={warning}>{warning}</div>)}
        </Alert>}
    </div>
);

export const LoadTestComparisonTable = ({ rows = [] }) => (
    <div className="table-responsive">
        <Table size="sm" bordered hover className="mb-0">
            <thead><tr><th>Métrica</th><th>Esperado / faixa</th><th>Observado</th><th>Diferença</th><th>Status</th></tr></thead>
            <tbody>
                {rows.map((row) => <tr key={row.id} className={row.tone || ''}>
                    <td>
                        {row.label}{row.metricKey && <div className="small text-muted">{row.metricKey}</div>}
                        {row.details?.length > 0 && <details className="small mt-1">
                            <summary>Detalhes da medição</summary>
                            {row.details.map(([label, value]) => <div key={label}><strong>{label}:</strong> {value}</div>)}
                        </details>}
                    </td>
                    <td>{row.expected}</td><td>{row.observed}</td><td>{row.difference}</td>
                    <td><LoadTestBadge variant={row.variant}>{row.status}</LoadTestBadge></td>
                </tr>)}
                {!rows.length && <tr><td colSpan={5} className="text-muted text-center">Sem comparação disponível.</td></tr>}
            </tbody>
        </Table>
    </div>
);

const LoadTestDiagnostic = ({ data }) => {
    const [copyStatus, setCopyStatus] = useState('');
    const json = useMemo(() => JSON.stringify(redactLoadTestDiagnostic(data), null, 2), [data]);
    const copy = async () => {
        try { await navigator.clipboard.writeText(json); setCopyStatus('JSON copiado.'); }
        catch { setCopyStatus('Não foi possível copiar. Selecione o JSON abaixo para copiá-lo.'); }
    };
    return <details className="mt-3">
        <summary className="fw-semibold">JSON do runner e diagnóstico</summary>
        <div className="d-flex flex-wrap align-items-center gap-2 mt-2 mb-2">
            <Button type="button" size="sm" variant="outline-secondary" onClick={copy}>Copiar JSON</Button>
            <small className="text-muted">Tokens, chaves e listas de e-mails são ocultados.</small>
            {copyStatus && <small role="status">{copyStatus}</small>}
        </div>
        <pre className="border rounded bg-light p-2 small" style={{ maxHeight: '24rem', overflow: 'auto', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{json}</pre>
    </details>;
};

export const LoadTestFlowModal = ({ show, onHide, flow, testLabel, onStart, onReset, cancelDescription, configuration, progress, results }) => {
    const { stage, busy, job, running, submitting, restoringSession, loadingReport, cancelling, finalData } = flow;
    const requestCancel = () => confirmAlert({
        closeOnEscape: false, closeOnClickOutside: false,
        customUI: ({ onClose }) => <ShowConfirm title="Encerrar teste?" description={cancelDescription}
            action={flow.cancel} onClose={onClose} textConfirm="Encerrar teste" />,
    });
    return <Modal
        key={stage} show={show} onHide={busy ? undefined : onHide} centered
        size={stage === 'result' ? 'xl' : stage === 'configuration' ? 'sm' : 'lg'}
        backdrop={busy ? 'static' : true} keyboard={!busy}
        className={`streaming-load-test-modal${stage === 'result' ? ' streaming-load-test-modal-results' : stage === 'configuration' ? '' : ' streaming-load-test-modal-progress'}`}
        style={{ zIndex: 1070 }} backdropClassName="streaming-load-test-backdrop"
    >
        <Modal.Header closeButton={!busy}>
            <Modal.Title>
                <FontAwesomeIcon icon={faFlask} className="me-2" />
                {stage === 'result' ? 'Resultado do teste' : stage === 'configuration' ? 'Testar captação de dados analíticos' : stage === 'analysis' ? 'Analisando resultado' : 'Teste em andamento'}
                <small className="d-block text-muted mt-1">{testLabel}</small>
            </Modal.Title>
        </Modal.Header>
        <Modal.Body>
            {stage === 'configuration' && configuration}
            {(submitting || restoringSession) && !job && <div className="text-center py-3">
                <Spinner animation="border" className="mb-3" /><div>{restoringSession ? 'Recuperando a sessão deste teste...' : 'Reservando o teste e preparando os bots...'}</div>
            </div>}
            {progress}
            {loadingReport && <div className="text-center py-4" role="status"><Spinner animation="border" size="sm" className="me-2" />Consolidando dados e analisando o resultado... Tentativa {flow.reportAttempt} de {LOAD_TEST_REPORT_ATTEMPTS}.</div>}
            {flow.error && <Alert variant="danger" className="p-2 d-block">{flow.error}</Alert>}
            {stage === 'result' && results}
            {(finalData || job) && <LoadTestDiagnostic key={finalData?.job?.id || job?.id || 'load-test-result'} data={finalData || { job }} />}
        </Modal.Body>
        <Modal.Footer>
            {stage === 'configuration' && <>
                <Button type="button" variant="secondary" onClick={onHide}>Cancelar</Button>
                <Button type="button" variant="primary" onClick={onStart}>Iniciar teste</Button>
            </>}
            {running && <Button type="button" variant="danger" onClick={requestCancel} disabled={cancelling}>
                <FontAwesomeIcon icon={faStop} className="me-1" />{cancelling ? 'Encerrando...' : 'Encerrar teste'}
            </Button>}
            {!busy && (stage === 'analysis' || flow.monitoringPaused) && <Button type="button" variant="outline-primary" onClick={flow.retry}>{stage === 'analysis' ? 'Tentar relatório novamente' : 'Tentar novamente'}</Button>}
            {!busy && ['analysis', 'result'].includes(stage) && <Button type="button" variant="outline-secondary" onClick={onReset}><FontAwesomeIcon icon={faRotate} className="me-1" />Novo teste</Button>}
            {!busy && stage !== 'configuration' && <Button type="button" variant="secondary" onClick={onHide}>Fechar</Button>}
        </Modal.Footer>
    </Modal>;
};
